import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, X, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import InstrumentSVG from "@/components/InstrumentSVG";
import { localePath, parseLocaleFromPath } from "@/lib/locale";
import { formatEurPlain } from "@/lib/money";

const LABELS = {
  hr: { title: "Vaša košarica", empty: "Vaša košarica je prazna.", remove: "Ukloni", total: "Ukupno", checkout: "Plati", continue: "Nastavi razgledavati" },
  en: { title: "Your cart", empty: "Your cart is empty.", remove: "Remove", total: "Total", checkout: "Checkout", continue: "Continue browsing" },
  de: { title: "Ihr Warenkorb", empty: "Ihr Warenkorb ist leer.", remove: "Entfernen", total: "Gesamt", checkout: "Zur Kasse", continue: "Weiter stöbern" },
  fr: { title: "Votre panier", empty: "Votre panier est vide.", remove: "Retirer", total: "Total", checkout: "Paiement", continue: "Continuer" },
};

export default function CartButton() {
  const { items, count, total, removeItem } = useCart();
  const { locale } = parseLocaleFromPath(useLocation().pathname);
  const L = LABELS[locale] || LABELS.hr;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="cart-button"
          aria-label="Cart"
          className="relative rounded-lg border border-border/60 p-2 hover:bg-muted transition-colors"
        >
          <ShoppingBag className="h-4 w-4" strokeWidth={1.5} />
          {count > 0 && (
            <span
              data-testid="cart-count-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-medium flex items-center justify-center px-1"
            >
              {count}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="bg-card border-border w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl">{L.title}</SheetTitle>
          <SheetDescription>{count} {count === 1 ? "item" : "items"}</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-10" data-testid="cart-empty">
            <ShoppingBag className="h-10 w-10 text-muted-foreground/40 mb-3" strokeWidth={1.2} />
            <p className="text-sm text-muted-foreground">{L.empty}</p>
          </div>
        ) : (
          <>
            <ul className="flex-1 overflow-y-auto space-y-3 py-2 pr-1" data-testid="cart-items">
              {items.map((it) => (
                <li key={it.id} data-testid={`cart-line-${it.id}`} className="rounded-lg border border-border/60 bg-background p-3 flex gap-3">
                  <div className="w-16 h-20 shrink-0 rounded-md overflow-hidden bg-muted">
                    <InstrumentSVG type={it.product_slug} config={it.preview_config || {}} engravingText={it.engraving_text || ""} testid={`cart-svg-${it.id}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-base text-foreground leading-tight truncate">{it.product_name}</div>
                    <ul className="mt-1 text-[11px] text-muted-foreground space-y-0.5">
                      {(it.configuration || []).filter((c) => c.value_label).slice(0, 3).map((c) => (
                        <li key={c.field_id} className="truncate">
                          {c.field_label}: <span className="text-foreground/80">{c.value_label}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-sm text-foreground font-medium">{formatEurPlain(it.unit_price_eur)}</span>
                      <button
                        type="button"
                        onClick={() => removeItem(it.id)}
                        data-testid={`cart-remove-${it.id}`}
                        className="inline-flex items-center gap-1 text-xs text-destructive hover:text-destructive/80"
                      >
                        <Trash2 className="h-3 w-3" strokeWidth={1.6} /> {L.remove}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="border-t border-border/60 pt-4 space-y-3">
              <div className="flex items-baseline justify-between" data-testid="cart-total">
                <span className="text-sm text-muted-foreground">{L.total}</span>
                <span className="font-serif text-2xl text-primary">{formatEurPlain(total)}</span>
              </div>
              <Link to={localePath(locale, "/checkout")} data-testid="cart-checkout-link">
                <Button className="w-full h-11 shadow-interactive">{L.checkout}</Button>
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
